import { useId } from "react";

import { SCORECARD_FIELD_LABEL } from "../../../constants";
import type { CriterionComparisonValueMode } from "../../../criterionConfig";

import { ScorecardSourceSelector } from "./ScorecardSourceSelector";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SmartColumn } from "@/queries/smart-tables/types";
import { cn } from "@/lib/utils";

interface CriterionComparisonValueFieldProps {
  mode: CriterionComparisonValueMode;
  staticValue: string;
  columnId: string;
  columns: SmartColumn[];
  onModeChange: (mode: CriterionComparisonValueMode) => void;
  onStaticValueChange: (value: string) => void;
  onColumnChange: (columnId: string) => void;
  modeLabel?: string;
  staticModeLabel?: string;
  columnModeLabel?: string;
  staticLabel?: string;
  columnLabel?: string;
  staticPlaceholder?: string;
  staticDescription?: string;
  columnDescription?: string;
}

const CriterionComparisonValueField = ({
  mode,
  staticValue,
  columnId,
  columns,
  onModeChange,
  onStaticValueChange,
  onColumnChange,
  modeLabel = "Match against",
  staticModeLabel = "Fixed text",
  columnModeLabel = "Column value",
  staticLabel = "Text to find",
  columnLabel = "Column to match",
  staticPlaceholder = "Enter the text that should appear in the source…",
  staticDescription = "Checks whether the source column contains this exact text.",
  columnDescription = "Uses each row's value from this column as the text to search for.",
}: CriterionComparisonValueFieldProps) => {
  const staticModeId = useId();
  const columnModeId = useId();

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className={SCORECARD_FIELD_LABEL}>{modeLabel}</Label>
        <div className="grid grid-cols-2 gap-1 rounded-lg border border-border/70 bg-muted/20 p-1">
          <button
            id={staticModeId}
            type="button"
            onClick={() => onModeChange("static")}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              mode === "static"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {staticModeLabel}
          </button>
          <button
            id={columnModeId}
            type="button"
            onClick={() => onModeChange("column")}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              mode === "column"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {columnModeLabel}
          </button>
        </div>
      </div>

      {mode === "static" ? (
        <div className="space-y-2">
          <Label htmlFor={`${staticModeId}-input`} className={SCORECARD_FIELD_LABEL}>
            {staticLabel}
          </Label>
          <Input
            id={`${staticModeId}-input`}
            value={staticValue}
            onChange={event => onStaticValueChange(event.target.value)}
            placeholder={staticPlaceholder}
          />
          <p className="text-xs text-muted-foreground">{staticDescription}</p>
        </div>
      ) : (
        <div className="space-y-2">
          <ScorecardSourceSelector
            value={columnId}
            onValueChange={onColumnChange}
            placeholder="Select a source..."
            options={columns.map(column => ({ id: column.id, title: column.title }))}
            name={columnLabel}
          />
          <p className="text-xs text-muted-foreground">{columnDescription}</p>
        </div>
      )}
    </div>
  );
};

export default CriterionComparisonValueField;
